import { useEffect } from 'react';

export type LazyObserverCallback = (el: HTMLElement, entry: IntersectionObserverEntry, observer: IntersectionObserver) => void;

// 单例模式，全局只存在一个懒加载观察者
class LazyObserver {
    private observer: IntersectionObserver;
    private callbacks = new WeakMap<Element, LazyObserverCallback>();
    constructor() {
        this.observer = new IntersectionObserver(this.handleIntersect, {
            rootMargin: '0px 0px 100% 0px', // 提前加载一屏
        });
    }
    private handleIntersect = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const callback = this.callbacks.get(entry.target);
                if (callback) {
                    callback(entry.target as HTMLElement, entry, this.observer);
                    // this.unobserve(entry.target); // 加载后取消观察
                }
            }
        });
    };
    observe(el: Element, callback: LazyObserverCallback) {
        this.callbacks.set(el, callback);
        this.observer.observe(el);
    }
    unobserve(el: Element) {
        this.observer.unobserve(el);
        this.callbacks.delete(el);
    }
}

let lazyObserver: LazyObserver | null = null;

export function useLazyObserver(ref: React.RefObject<HTMLElement>, callback: LazyObserverCallback, lazy?: boolean) {
    useEffect(() => {
        if (typeof window === 'undefined' || !lazy) return; // ✅ 避免 SSR 报错
        if (!lazyObserver) {
            lazyObserver = new LazyObserver();
        }

        const el = ref.current;
        if (el) {
            lazyObserver.observe(el, callback);
        }

        return () => {
            if (el) {
                lazyObserver?.unobserve(el);
            }
        };
    }, [ref, callback, lazy]);
}
